(() => {
  const cents = value => {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const amount = Number(value);
    return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : null;
  };
  function calculate(config, variant) {
    const originalOnly = config.priceDisplay === 'original';
    const productPrice = config.productPrices?.[variant];
    const base = originalOnly
      ? (Number.isInteger(productPrice) && productPrice >= 0 ? productPrice : null)
      : cents(config.variants?.[variant]) ?? cents(config.base);
    if (base === null) return null;
    const tiers = config.tiers.map((tier, index) => ({ index, spend: cents(tier.spend), save: cents(tier.save), code: String(tier.code || '').trim() }));
    if (tiers.some(tier => tier.spend === null || tier.save === null || tier.save > tier.spend)) return null;
    const selected = tiers.filter(tier => base >= tier.spend && (config.mode !== 'code' || tier.code)).sort((a, b) => b.spend - a.spend)[0];
    return { base, price: originalOnly ? base : base - (selected?.save || 0), saving: originalOnly ? 0 : selected?.save || 0, selected: selected?.index ?? -1, tiers };
  }
  function endTimestamp(value) {
    // Require an explicit UTC offset; visitors' local timezones must not change the deadline.
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})$/.test(value || '') ? Date.parse(value) : NaN;
  }
  async function copyText(text) {
    // The theme-editor iframe may deny Clipboard API access. Try the
    // selection-based path synchronously while the click is still active.
    const previousFocus = document.activeElement;
    const selection = document.getSelection?.();
    const ranges = [];
    if (selection) for (let i = 0; i < selection.rangeCount; i++) ranges.push(selection.getRangeAt(i).cloneRange());
    let input;
    let copied = false;
    try {
      input = document.createElement('textarea');
      input.value = text;
      input.readOnly = true;
      input.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;font-size:16px;';
      document.body.appendChild(input);
      input.focus({ preventScroll: true });
      input.select();
      input.setSelectionRange(0, text.length);
      copied = document.execCommand?.('copy') === true;
    } catch {
      copied = false;
    } finally {
      input?.remove();
      if (selection && ranges.length) {
        selection.removeAllRanges();
        ranges.forEach(range => selection.addRange(range));
      }
      previousFocus?.focus({ preventScroll: true });
    }
    if (copied) return;
    if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
    await navigator.clipboard.writeText(text);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { calculate, endTimestamp, copyText };
  if (typeof customElements === 'undefined' || customElements.get('pixcut-halloween-offer')) return;
  customElements.define('pixcut-halloween-offer', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      clearInterval(this.timer);
      this.abort = new AbortController();
      this.root = this.closest('.pxs-pdp');
      try { this.config = JSON.parse(this.querySelector('[data-offer-config]').textContent); } catch { return; }
      this.renderedPriceKey = null;
      this.end = endTimestamp(this.dataset.end);
      this.variant = this.dataset.variant;
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: 'USD' });
      this.root?.addEventListener('pxs:variant-change', event => {
        this.variant = String(event.detail.variantId);
        this.update();
      }, { signal: this.abort.signal });
      window.addEventListener('pageshow', () => this.update(), { signal: this.abort.signal });
      document.addEventListener('visibilitychange', () => this.update(), { signal: this.abort.signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-offer-copy]');
        if (button && this.contains(button)) this.copyCode(button);
      }, { signal: this.abort.signal });
      this.update();
      if (this.dataset.enabled === 'true' && this.end > Date.now() && this.dataset.currency === 'USD') {
        this.timer = setInterval(() => this.update(), 1000);
      }
    }
    update() {
      const result = calculate(this.config, this.variant);
      const active = this.dataset.enabled === 'true' && this.dataset.currency === 'USD' && this.end > Date.now() && result !== null;
      this.hidden = !active;
      this.root?.classList.toggle('pxs-halloween-active', active && this.dataset.hidePrice === 'true');
      if (!active) {
        if (!(this.end > Date.now())) clearInterval(this.timer);
        return;
      }
      // Do not rewrite amounts every second: currency apps can convert them
      // once without the countdown repeatedly replacing them with USD.
      const priceKey = JSON.stringify([result, this.dataset.mode]);
      if (this.renderedPriceKey !== priceKey) {
        const format = amount => this.money.format(amount / 100);
        this.querySelector('[data-offer-original]').textContent = format(result.base);
        this.querySelector('[data-offer-original]').hidden = result.saving === 0;
        this.querySelector('[data-offer-price]').textContent = format(result.price);
        this.querySelector('[data-offer-saving]').textContent = result.saving ? 'Save ' + format(result.saving) : '';
        const caption = this.querySelector('[data-offer-caption]');
        if (caption) caption.hidden = this.config.priceDisplay === 'original';
        this.querySelector('[data-offer-saving]').hidden = this.config.priceDisplay === 'original';
        this.querySelectorAll('[data-offer-tier]').forEach((row, index) => {
          const tier = result.tiers[index];
          row.querySelector('[data-tier-label]').textContent = 'Spend ' + format(tier.spend) + ', Save ' + format(tier.save);
          row.querySelector('[data-tier-check]').hidden = this.dataset.mode === 'code' || result.selected !== index;
          row.classList.toggle('is-selected', result.selected === index);
        });
        this.renderedPriceKey = priceKey;
      }
      const seconds = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
      const values = { days: Math.floor(seconds / 86400), hours: Math.floor(seconds / 3600) % 24, minutes: Math.floor(seconds / 60) % 60, seconds: seconds % 60 };
      Object.entries(values).forEach(([key, value]) => { this.querySelector('[data-' + key + ']').textContent = String(value).padStart(2, '0'); });
    }
    async copyCode(button) {
      this.update();
      const code = button.dataset.code?.trim();
      if (this.hidden || button.disabled || !code) return;
      const status = this.querySelector('[data-copy-status]');
      button.disabled = true;
      try {
        await copyText(code);
        if (!this.isConnected) return;
        button.textContent = this.dataset.copiedLabel || 'Copied!';
        button.dataset.copied = 'true';
        status.textContent = '';
      } catch {
        if (!this.isConnected) return;
        button.textContent = this.dataset.copyLabel || 'Copy Code';
        delete button.dataset.copied;
        status.textContent = 'Unable to copy. Please select and copy this code: ' + code;
      } finally {
        button.disabled = false;
      }
      this.copyTimers ??= new Map();
      clearTimeout(this.copyTimers.get(button));
      this.copyTimers.set(button, setTimeout(() => {
        button.textContent = this.dataset.copyLabel || 'Copy Code';
        delete button.dataset.copied;
        this.copyTimers.delete(button);
      }, 4000));
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.abort?.abort();
      this.copyTimers?.forEach(timer => clearTimeout(timer));
      this.copyTimers?.clear();
      this.root?.classList.remove('pxs-halloween-active');
    }
  });
})();
